import React, { Component, ReactNode } from 'react';
import ItemToolsMenu from '../../ItemToolsMenu';
import CustomSidebarLayout from '../../custom/CustomSidebarLayout';
import { MenuPermission, Role } from '../../../domain/rbac/RbacDomains';
import CustomLoading from '../../custom/CustomLoading';
import RoleListComponent from './RoleListComponent';
import RoleEditComponent from './RoleEditComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import RoleService from '../../../service/RoleService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import ValueFactory from '../../../util/ValueFactory';
import _ from 'lodash';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import RoleHelper from '../../../RoleHelper';
import ComponentName from '../../../constant/ComponentName';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { CustomFilterFields } from '../../../domain/ui/FilterDomains';
import FilterUtil from '../../../util/FilterUtil';
import { dispatch } from '../../../redux/store';
import { changePageFilter } from '../../../redux/actions/PageActions';
import Filter = query.Filter;
import empty = query.empty;
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import and = query.and;
import eq = query.eq;
import QueryBuilder = query.QueryBuilder;
import LanguageUtil from '../../../util/LanguageUtil';

class RoleContainer extends Component<Props, State> {
  public state = new State();

  private roleSubscription: subscribe.Subscription | undefined;
  private checkerRequestSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private readonly roleService: RoleService = ServiceContainer.getInstance().get(ServiceNames.ROLE);
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const { visible, loading, selectedRole, isCreate, roles, rows, filter, currentPage, checkerRequests, previewUserUpdateRequest } = this.state;
    return loading ? (
      <CustomLoading />
    ) : (
      <div className={'flex flex-col w-full h-full px-5'}>
        <div className={'flex min-h-[24px] justify-start bg-white items-center w-full'}>
          <ItemToolsMenu
            createFunc={this.openCreatePage}
            onChangeFilter={this.onChangeFilter}
            filter={filter}
            exportVisible={false}
            defaultFilterDisabled={true}
            importVisible={false}
            createVisible={!this.environmentUtil.isLdapOnly()}
            component={ComponentName.ADMIN_ROLE}
            componentLabel={LanguageUtil.get({ id: 'pages.admin.role.title', defaultMessage: 'Role Management' })}
          />
        </div>
        <div className={'flex w-full h-full'}>
          <div className={'w-full h-full flex flex-col lg:!max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
            {roles && (
              <RoleListComponent
                setSelectedRole={this.onChangeSelectedRole}
                openEditor={this.openUpdatePage}
                selectedRole={selectedRole}
                roles={roles}
                checkerRequests={checkerRequests}
                updateRequestData={previewUserUpdateRequest}
                setUpdateRequestData={(data: CheckerRequest<Role> | undefined) => this.setState({ previewUserUpdateRequest: data })}
                onDelete={this.onDeleteRole}
              />
            )}
            <CustomPaginator
              rows={rows}
              currentPage={currentPage}
              currentRecords={roles.length}
              rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
              totalRecords={this.state.totalRecord}
              onPageChange={this.onPageChange}
            />
          </div>
          <CustomSidebarLayout
            content={
              <RoleEditComponent
                isCreate={isCreate}
                onRoleMenuChange={this.onRoleMenuChange}
                isCreateChange={this.isCreateChange}
                selectedRole={selectedRole}
                onHide={this.onChangeVisible}
                onRoleChange={this.onChangeSelectedRole}
                checkerRequests={checkerRequests}
              />
            }
            title={LanguageUtil.get({ id: 'pages.admin.role.editor.title', defaultMessage: 'Role Editor' })}
            visible={visible}
            closable={false}
            onHide={this.onChangeVisible}
            icon={'groups'}
          />
          {previewUserUpdateRequest ? (
            <CustomSidebarLayout
              content={
                <RoleEditComponent
                  isCreate={isCreate}
                  onRoleMenuChange={this.onRoleMenuChange}
                  isCreateChange={this.isCreateChange}
                  selectedRole={previewUserUpdateRequest?.metadata as Role}
                  onHide={this.onChangeVisible}
                  onRoleChange={this.onChangeSelectedRole}
                  checkerRequests={checkerRequests}
                  preview
                />
              }
              title={'Role'}
              visible={visible}
              closable={false}
              onHide={this.onChangeVisible}
              icon={'groups'}
              position='left'
            />
          ) : null}
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.prepareRoleCount();
    this.prepareFilterFields();
    this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = () => {
    const { rows, currentPage, filter } = this.state;
    let roleQuery: query.SelectQueryBuilder = query.QueryBuilder.select()
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .from(CollectionNames.ROLES)
      .where(filter);
    const checkerRequestQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.CHECKER_REQUESTS)
      .where(and(eq('component', ComponentName.ADMIN_ROLE), eq('status', 'Waiting')))
      .build();
    this.roleSubscription = subscribe.Formica.subscribe(CollectionNames.ROLES, roleQuery.build());
    this.checkerRequestSubscription = subscribe.Formica.subscribe(CollectionNames.CHECKER_REQUESTS, checkerRequestQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.roleSubscription, this.checkerRequestSubscription]);
  };

  private tracker = async () => {
    const { rows, filter } = this.state;
    if (this.roleSubscription?.ready()) {
      await this.prepareRoleCount();
      const roleQuery: query.Query = query.QueryBuilder.select().limit(rows).from(CollectionNames.ROLES).where(filter).build();
      const checkerRequestQuery = QueryBuilder.select().from(CollectionNames.CHECKER_REQUESTS).build();
      const roles: Role[] = await this.repository.findMany(roleQuery);
      const checkerRequests: CheckerRequest[] = await this.repository.findMany(checkerRequestQuery);
      this.setState({ roles: roles, checkerRequests, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.roleSubscription?.stop();
    await this.checkerRequestSubscription?.stop();
    this.computation?.stop();
  };

  private reSubscribe = async () => {
    if (this.computation != undefined) {
      this.computation.onStop(() => {
        this.startTracker();
      });
      await this.stopTracker();
    } else {
      await this.stopTracker();
      this.startTracker();
    }
  };

  private prepareRoleCount = async () => {
    const { filter } = this.state;
    try {
      const _query = query.QueryBuilder.select().from(CollectionNames.ROLES).where(filter).build();
      const count = await this.roleService.getCount(_query);
      this.setState({ totalRecord: count });
    } catch (e) {
      console.error(e);
    }
  };

  private prepareFilterFields = async () => {
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.ADMIN_ROLE);
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  private onDeleteRole = async (name: string) => {
    await this.roleService.deleteRole(name);
    await this.prepareRoleCount();
  };

  private onRoleMenuChange = (menu: MenuPermission[]) => {
    const { selectedRole } = this.state;
    selectedRole.menu = menu;
    this.setState({ selectedRole });
  };

  private openCreatePage = () => {
    if (!RoleHelper.canManage('Role')) return;
    this.setState({ isCreate: true, visible: true });
  };

  private openUpdatePage = () => {
    this.setState({ isCreate: false, visible: true });
  };

  public onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page }, this.reSubscribe);
  };

  private onChangeVisible = () => {
    const { isCreate } = this.state;
    if (!RoleHelper.canManage('Role') && isCreate) return;
    this.setState({ visible: false, selectedRole: ValueFactory.initializeRole() });
  };

  private onChangeSelectedRole = (role: Role) => {
    if (!RoleHelper.canManage('Role')) return;
    this.setState({ selectedRole: role });
  };

  private onChangeFilter = (filter: Filter) => {
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    this.setState({ filter }, this.reSubscribe);
  };

  private isCreateChange = () => {
    this.setState({
      isCreate: false,
    });
  };
}

interface Props {}

class State {
  roles: Role[] = [];
  checkerRequests: CheckerRequest[] = [];

  visible = false;
  loading = true;
  isCreate = false;
  rows = 10;
  totalRecord = 0;
  currentPage = 1;
  selectedRole: Role = ValueFactory.initializeRole();
  filter: Filter = empty();
  previewUserUpdateRequest: CheckerRequest<Role> | undefined;
}

export default RoleContainer;
